import type { FC } from "react";
import { useState } from "react";

import {
  useCancelSubscription,
  useGetSubscriptionInfo,
  useReactivateSubscription,
} from "@/api/stripe";
import DialogCustom from "@/components/modals/dialog-custom";
import SubscriptionTierModal from "@/components/modals/profile/subscription-tier-modal";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { Separator } from "@/components/ui/separator";
import { getSubscriptionPriceInfo } from "@/types/api/user";
import { capitalizeFirst } from "@/utils/string";

type SubscriptionSideModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const SubscriptionSideModal: FC<SubscriptionSideModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: subscription, isLoading } = useGetSubscriptionInfo();
  const { mutateAsync: cancelSubscription, isPending: isCancelling } =
    useCancelSubscription();
  const { mutateAsync: reactivateSubscription, isPending: isReactivating } =
    useReactivateSubscription();

  if (isLoading) {
    return (
      <>
        <DialogCustom
          title="Abonnement"
          description={
            <div className="text-sm text-gray-600">
              {"Gestion de mon abonnement"}
            </div>
          }
          open={open}
          onOpenChange={onOpenChange}
          side
        >
          <Loader />
        </DialogCustom>
      </>
    );
  }

  const subscriptionInfo = getSubscriptionPriceInfo(
    subscription.items.data[0].price.id
  );

  return (
    <>
      <DialogCustom
        title="Abonnement"
        description={
          <div className="text-sm text-gray-600">
            {"Gestion de mon abonnement"}
          </div>
        }
        open={open}
        onOpenChange={onOpenChange}
        side
      >
        <Separator />
        <div className="flex h-full flex-col gap-5 p-8 bg-grey-25">
          <div>
            <div className="text-xs text-[#64748B]">{"Mon abonnement"}</div>
            <div className="text-sm">{`${capitalizeFirst(subscriptionInfo.label)} ${subscriptionInfo.monthly ? "mensuel" : "annuel"}`}</div>
            <div className="text-sm">{`${subscriptionInfo.monthly ? subscriptionInfo.monthlyPrice : subscriptionInfo.yearlyPrice}€/${subscriptionInfo.monthly ? "mois" : "an"}`}</div>
          </div>
          <div className="text-sm">{`Abonnement commencé le ${new Date(subscription.start_date * 1000).toLocaleDateString()}`}</div>
          <div className="text-sm">{`Prochain prélèvement le ${new Date(subscription.items.data[0].current_period_end * 1000).toLocaleDateString()}`}</div>
          {subscription.cancel_at ? (
            <div className="text-sm">{`Annulation prévue le ${new Date(subscription.cancel_at * 1000).toLocaleDateString()}`}</div>
          ) : null}
        </div>
        <Separator />
        <div className="flex flex-col pt-6 px-6 gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setIsModalOpen(true)}
          >
            Modifier mon abonnement
          </Button>
          {subscription.cancel_at ? (
            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={async () => {
                await reactivateSubscription();
              }}
              loading={isReactivating}
            >
              Réactiver mon abonnement
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              className="w-full"
              onClick={async () => {
                await cancelSubscription();
              }}
              loading={isCancelling}
            >
              Annuler mon abonnement
            </Button>
          )}
        </div>
      </DialogCustom>
      <SubscriptionTierModal
        currentPriceId={subscription.items.data[0].price.id}
        open={isModalOpen}
        setOpen={setIsModalOpen}
      />
    </>
  );
};

export default SubscriptionSideModal;
